export interface GameControlsConfig {
  coinValues: number[]
  defaultBet: number
  minBet: number
  maxBet: number
  paylineCount: number | null // null for ways/cluster
  gridType: 'paylines' | 'ways' | 'cluster'
}

export interface AutoSpinConfig {
  count: number // 0 = infinite
  stopOnAnyWin: boolean
  stopOnBonusTrigger: boolean
  stopOnBalanceBelow: number // 0 = disabled
  stopOnWinAbove: number // 0 = disabled
}

export type SpinButtonState = 'ready' | 'spinning' | 'disabled'

const DEFAULT_AUTO_SPIN_CONFIG: AutoSpinConfig = {
  count: 0,
  stopOnAnyWin: false,
  stopOnBonusTrigger: false,
  stopOnBalanceBelow: 0,
  stopOnWinAbove: 0,
}

/**
 * GameControls manages the state and logic for slot game controls:
 *   - Bet adjustment (coin value up/down, presets)
 *   - Spin button state tracking
 *   - Auto-spin with configurable stop conditions
 *   - Turbo mode toggle
 *
 * This is a pure logic class (no PixiJS or DOM dependency) — the Vue
 * template reads its state to render the UI.
 */
export class GameControls {
  private readonly _coinValues: number[]
  private _coinIndex: number
  private readonly _paylineCount: number | null
  private readonly _gridType: string
  private readonly _minBet: number
  private readonly _maxBet: number

  private _turboEnabled: boolean = false
  private _spinButtonState: SpinButtonState = 'ready'

  // Auto-spin
  private _autoSpinActive: boolean = false
  private _autoSpinRemaining: number = 0
  private _autoSpinConfig: AutoSpinConfig = { ...DEFAULT_AUTO_SPIN_CONFIG }

  constructor(config: GameControlsConfig) {
    if (!config.coinValues || config.coinValues.length === 0) {
      throw new Error('GameControls: coinValues must be a non-empty array')
    }
    if (config.minBet <= 0) {
      throw new Error('GameControls: minBet must be positive')
    }
    if (config.maxBet <= 0 || config.maxBet < config.minBet) {
      throw new Error('GameControls: maxBet must be positive and >= minBet')
    }

    this._coinValues = [...config.coinValues].sort((a, b) => a - b)
    this._paylineCount = config.paylineCount
    this._gridType = config.gridType
    this._minBet = config.minBet
    this._maxBet = config.maxBet

    // Find the coin index that produces totalStake closest to defaultBet
    const targetCoin =
      this._paylineCount && this._paylineCount > 0
        ? config.defaultBet / this._paylineCount
        : config.defaultBet
    this._coinIndex = this.findClosestCoinIndex(targetCoin)
    this.clampCoinToBetLimits()
  }

  // ─── Coin / Bet getters ───

  get coinValue(): number {
    return this._coinValues[this._coinIndex]
  }

  get coinIndex(): number {
    return this._coinIndex
  }

  get coinValues(): number[] {
    return [...this._coinValues]
  }

  get totalStake(): number {
    return this._paylineCount && this._paylineCount > 0
      ? this.roundCents(this.coinValue * this._paylineCount)
      : this.coinValue
  }

  get paylineCount(): number | null {
    return this._paylineCount
  }

  get gridType(): string {
    return this._gridType
  }

  get minBet(): number {
    return this._minBet
  }

  get maxBet(): number {
    return this._maxBet
  }

  get canCoinUp(): boolean {
    return this._coinIndex < this._coinValues.length - 1
  }

  get canCoinDown(): boolean {
    return this._coinIndex > 0
  }

  // ─── Coin value controls ───

  coinUp(): boolean {
    if (!this.canCoinUp) return false
    const prevIndex = this._coinIndex
    this._coinIndex++
    this.clampCoinToBetLimits()
    return this._coinIndex !== prevIndex
  }

  coinDown(): boolean {
    if (!this.canCoinDown) return false
    this._coinIndex--
    return true
  }

  setCoinIndex(index: number): void {
    this._coinIndex = Math.max(0, Math.min(index, this._coinValues.length - 1))
    this.clampCoinToBetLimits()
  }

  // ─── Bet presets ───

  setMinBet(): void {
    this._coinIndex = 0
  }

  setMaxBet(): void {
    this._coinIndex = this._coinValues.length - 1
    this.clampCoinToBetLimits()
  }

  /** Set bet to approximately N× the minimum bet */
  setBetMultiple(multiple: number): void {
    const targetStake = this._minBet * multiple
    const targetCoin =
      this._paylineCount && this._paylineCount > 0
        ? targetStake / this._paylineCount
        : targetStake
    this._coinIndex = this.findClosestCoinIndex(targetCoin)
    this.clampCoinToBetLimits()
  }

  // ─── Spin button ───

  get spinButtonState(): SpinButtonState {
    return this._spinButtonState
  }

  get isSpinReady(): boolean {
    return this._spinButtonState === 'ready'
  }

  get isSpinning(): boolean {
    return this._spinButtonState === 'spinning'
  }

  setSpinning(): void {
    this._spinButtonState = 'spinning'
  }

  setReady(): void {
    this._spinButtonState = 'ready'
  }

  setDisabled(): void {
    this._spinButtonState = 'disabled'
  }

  // ─── Turbo mode ───

  get turboEnabled(): boolean {
    return this._turboEnabled
  }

  toggleTurbo(): boolean {
    this._turboEnabled = !this._turboEnabled
    return this._turboEnabled
  }

  setTurbo(enabled: boolean): void {
    this._turboEnabled = enabled
  }

  // ─── Auto-spin ───

  get autoSpinActive(): boolean {
    return this._autoSpinActive
  }

  get autoSpinRemaining(): number {
    return this._autoSpinRemaining
  }

  get autoSpinConfig(): AutoSpinConfig {
    return { ...this._autoSpinConfig }
  }

  startAutoSpin(config: Partial<AutoSpinConfig>): void {
    this._autoSpinConfig = { ...DEFAULT_AUTO_SPIN_CONFIG, ...config }
    this._autoSpinRemaining = this._autoSpinConfig.count
    this._autoSpinActive = true
  }

  /**
   * Check stop conditions after a spin completes.
   * @returns true if auto-spin should continue, false if it should stop
   */
  processAutoSpin(
    won: boolean,
    winAmount: number,
    balance: number,
    bonusTriggered: boolean,
  ): boolean {
    if (!this._autoSpinActive) return false

    // Check stop conditions
    if (this._autoSpinConfig.stopOnAnyWin && won) {
      this.stopAutoSpin()
      return false
    }
    if (this._autoSpinConfig.stopOnBonusTrigger && bonusTriggered) {
      this.stopAutoSpin()
      return false
    }
    if (
      this._autoSpinConfig.stopOnBalanceBelow > 0 &&
      balance < this._autoSpinConfig.stopOnBalanceBelow
    ) {
      this.stopAutoSpin()
      return false
    }
    if (this._autoSpinConfig.stopOnWinAbove > 0 && winAmount > this._autoSpinConfig.stopOnWinAbove) {
      this.stopAutoSpin()
      return false
    }

    // Check insufficient balance for next spin
    if (balance < this.totalStake) {
      this.stopAutoSpin()
      return false
    }

    // Decrement counter (0 = infinite)
    if (this._autoSpinRemaining > 0) {
      this._autoSpinRemaining--
      if (this._autoSpinRemaining <= 0) {
        this.stopAutoSpin()
        return false
      }
    }

    return true
  }

  stopAutoSpin(): void {
    this._autoSpinActive = false
    this._autoSpinRemaining = 0
  }

  // ─── Helpers ───

  private findClosestCoinIndex(target: number): number {
    let bestIdx = 0
    let bestDiff = Math.abs(this._coinValues[0] - target)
    for (let i = 1; i < this._coinValues.length; i++) {
      const diff = Math.abs(this._coinValues[i] - target)
      if (diff < bestDiff) {
        bestDiff = diff
        bestIdx = i
      }
    }
    return bestIdx
  }

  private clampCoinToBetLimits(): void {
    // Clamp down if total stake exceeds maxBet
    while (this._coinIndex > 0 && this.totalStake > this._maxBet) {
      this._coinIndex--
    }
  }

  private roundCents(value: number): number {
    return Math.round(value * 100) / 100
  }
}
